import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {formatPrice} from '../helpers/PriceHelper';
import {getDeadlineDate, formatDeadlineShort} from './fields/Urgency';
import AppliedCouponBadge from './AppliedCouponBadge';
import AnimatedNumber from '../../common/AnimatedNumber';
import {checkCoupon, clearCoupon, COUPON_STATUS_PENDING} from '../actions/CouponActions';
import {clearCouponFromUrl} from '../helpers/ResumeFormUrlStateHelper';

export default function SummarySidebar({
                                           options,
                                           calculator,
                                           values,
                                           page = 0,
                                           hasError,
                                           showResetPassword = false,
                                           customerBalance = 0,
                                           useBalance = false,
                                           onUseBalanceChange,
                                           isAccountPage = false
                                       }) {
    const dispatch = useDispatch();
    const geoBlocked = useSelector(state => !!state.user?.geoBlocked);
    const couponState = useSelector(state => state.coupon);
    const {services, features, subtotal, discount, totalPrice, coupon, balanceApplied = 0} = calculator;
    const couponApplied = !!(coupon && coupon.data && coupon.data.isValid && discount > 0);
    const balanceAvailable = parseFloat(customerBalance) > 0;
    const balanceShown = useBalance && balanceApplied > 0;
    const [mobileOpen, setMobileOpen] = useState(false);
    const [couponInput, setCouponInput] = useState('');
    const couponPending = couponState?.status === COUPON_STATUS_PENDING;

    const applyCoupon = () => {
        const code = couponInput.trim();
        if (!code || couponPending) return;
        dispatch(checkCoupon(code));
    };

    const removeCoupon = () => {
        dispatch(clearCoupon());
        clearCouponFromUrl();
        setCouponInput('');
    };

    const selectedServices = values.services || [];
    const selectedFeatures = values.features || [];
    const hasItems = selectedServices.length > 0 || selectedFeatures.length > 0;

    const careerName = options.careerLevel?.[values.careerLevel]?.name || 'None';
    const deadline = getDeadlineDate(values.urgency, options.urgency);

    const itemCount = selectedServices.length + selectedFeatures.length;

    const sidebarContent = <>
        <div className="resume-list-content__header">
            <div className="resume-form__title">Order Summary</div>
            {itemCount > 0 && (
                <div className="resume-list-content__header-count">
                    {itemCount} {itemCount === 1 ? 'item' : 'items'}
                </div>
            )}
        </div>

        <div className="resume-list-content__meta">
            <div className="resume-list-content__meta-item">
                <div className="resume-list-content__meta-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                    </svg>
                </div>
                <div className="resume-list-content__meta-text">
                    <div className="resume-list-content__meta-label">Career Level</div>
                    <div className="resume-list-content__meta-value">{careerName}</div>
                </div>
            </div>
            <div className="resume-list-content__meta-item">
                <div className="resume-list-content__meta-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                    </svg>
                </div>
                <div className="resume-list-content__meta-text">
                    <div className="resume-list-content__meta-label">Deadline</div>
                    <div className="resume-list-content__meta-value">{formatDeadlineShort(deadline) || '—'}</div>
                </div>
            </div>
        </div>

        <div className="resume-list-content__items">
            {!hasItems && (
                <div className="resume-list-content__empty">
                    <div className="resume-list-content__empty-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="9" cy="21" r="1"/>
                            <circle cx="20" cy="21" r="1"/>
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                        </svg>
                    </div>
                    <div className="resume-list-content__empty-title">Your order is empty</div>
                    <div className="resume-list-content__empty-sub">Select services and features to get started</div>
                </div>
            )}

            {selectedServices.length > 0 && (
                <div className="resume-list-content__group">
                    <div className="resume-list-content__group-title">
                        <span>Services</span>
                        <span className="resume-list-content__group-count">{selectedServices.length}</span>
                    </div>
                    <div className="resume-list-content__group-items">
                        {selectedServices.map(key => services[key] && (
                            <div className="resume-list-content__row" key={key}>
                                <div className="resume-list-content__row-check">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"/>
                                    </svg>
                                </div>
                                <div className="resume-list-content__label">{services[key].name}</div>
                                <div className="resume-list-content__value"><AnimatedNumber value={services[key].price} format={formatPrice}/></div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {selectedFeatures.length > 0 && (
                <div className="resume-list-content__group">
                    <div className="resume-list-content__group-title">
                        <span>Features</span>
                        <span className="resume-list-content__group-count">{selectedFeatures.length}</span>
                    </div>
                    <div className="resume-list-content__group-items">
                        {selectedFeatures.map(key => features[key] && (
                            <div className="resume-list-content__row" key={key}>
                                <div className="resume-list-content__row-check">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"/>
                                    </svg>
                                </div>
                                <div className="resume-list-content__label">{features[key].name}</div>
                                <div className="resume-list-content__value"><AnimatedNumber value={features[key].price} format={formatPrice}/></div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        {hasItems && !couponApplied && (
            <div className="resume-form__coupon">
                <div className="resume-form__coupon-row">
                    <input
                        type="text"
                        className="resume-form__coupon-input"
                        placeholder="Promo code"
                        value={couponInput}
                        onChange={e => setCouponInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                applyCoupon();
                            }
                        }}
                        disabled={couponPending}
                        maxLength={32}
                    />
                    <button
                        type="button"
                        className="resume-form__coupon-apply"
                        onClick={applyCoupon}
                        disabled={!couponInput.trim() || couponPending}
                    >
                        {couponPending ? 'Applying...' : 'Apply'}
                    </button>
                </div>
            </div>
        )}

        {/* Price breakdown keeps subtotal, coupon savings, and account credit
            in one aligned block so the right-side money column doesn't jump. */}
        {hasItems && (
            <div className="resume-form__discount">
                <div className="resume-form__discount-row">
                    <span className="resume-form__discount-label">Items total</span>
                    <span className="resume-form__discount-value"><AnimatedNumber value={subtotal} format={formatPrice}/></span>
                </div>
                {couponApplied && (
                    <AppliedCouponBadge
                        coupon={coupon}
                        discount={discount}
                        formatPrice={formatPrice}
                        onRemove={removeCoupon}
                    />
                )}
                {balanceAvailable && (
                    <div
                        className={`resume-form__discount-row resume-form__discount-row--applied ${balanceShown ? '' : 'resume-form__discount-row--hidden'}`}
                        aria-hidden={!balanceShown}
                    >
                        <span className="resume-form__discount-label">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <rect x="2" y="6" width="20" height="14" rx="2"/>
                                <path d="M2 10h20"/>
                            </svg>
                            Account credit
                        </span>
                        <span className="resume-form__discount-value resume-form__discount-value--negative">
                            -<AnimatedNumber value={balanceApplied} format={formatPrice}/>
                        </span>
                    </div>
                )}
            </div>
        )}

        {balanceAvailable && hasItems && (
            <label className="resume-form__balance-toggle">
                <input
                    type="checkbox"
                    checked={useBalance}
                    onChange={e => onUseBalanceChange && onUseBalanceChange(e.target.checked)}
                    hidden
                />
                <div className={`resume-checkbox-field ${useBalance ? 'checked' : ''}`}>
                    <span className="fa fa-check-square"></span>
                    <span className="fa fa-square-o"></span>
                </div>
                <span className="resume-form__balance-toggle-body">
                    <span className="resume-form__balance-toggle-title">Use my account credit</span>
                    <span className="resume-form__balance-toggle-amount-line">
                        <span className="resume-form__balance-toggle-sub">Available</span>
                        <span className={`resume-form__balance-toggle-amount ${useBalance ? 'checked' : ''}`}>
                            <AnimatedNumber value={customerBalance} format={formatPrice}/>
                        </span>
                    </span>
                </span>
            </label>
        )}

        <div className="resume-form__total-price">
            <div className="resume-form__price text-center">
                <strong className="label-price">Total</strong>
                <span className={`amount-price ${totalPrice <= 0 ? 'empty' : ''}`}>
                    <AnimatedNumber value={totalPrice} format={formatPrice}/>
                </span>
            </div>
            {!showResetPassword && (
                // Geo-blocked users can't pay regardless of which step
                // they're on. Same behavior as v5's PaymentButton inside
                // SummarySidebar / OrderForm — replace the CTA with a
                // disabled "not available" label so the user understands
                // why nothing happens, instead of silently disabling.
                geoBlocked ? (
                    <button
                        type="button"
                        className="button-proceed"
                        style={{opacity: 0.5, cursor: 'not-allowed'}}
                        disabled
                    >
                        Unavailable
                    </button>
                ) : (
                    <button className={`button-proceed ${page === 0 && hasError ? 'error' : ''}`}
                            type="submit" form="resume-wizard-form">
                        {(isAccountPage || page !== 0) ? 'Place An Order' : 'Proceed'}
                    </button>
                )
            )}
        </div>
    </>;

    return <>
        {/* Desktop sidebar */}
        <div className="resume-list-block">
            <div className="resume-list-content">
                {sidebarContent}
            </div>
        </div>

        {/* Mobile slide-up drawer */}
        <div className="resume-mobile-sidebar">
            <button
                className="resume-mobile-sidebar__trigger"
                type="button"
                onClick={() => setMobileOpen(!mobileOpen)}
            >
                <span className="resume-mobile-sidebar__trigger-text">View Order Details</span>
                <span className={`resume-mobile-sidebar__arrow ${mobileOpen ? 'open' : ''}`}/>
            </button>

            {/* Overlay */}
            {mobileOpen && (
                <div
                    className="resume-mobile-sidebar__overlay"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            <div className={`resume-mobile-sidebar__drawer ${mobileOpen ? 'open' : ''}`}>
                <div className="resume-mobile-sidebar__drawer-header">
                    <span>Order Summary</span>
                    <button
                        type="button"
                        className="resume-mobile-sidebar__close"
                        onClick={() => setMobileOpen(false)}
                    >
                        &times;
                    </button>
                </div>
                <div className="resume-mobile-sidebar__drawer-body">
                    {sidebarContent}
                </div>
            </div>
        </div>
    </>;
}
